// @file Composable for undoable actions
import { ActionHistoryType, redo, undo } from '@@/bits/action_history_manager'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { DraftPost, Post } from '@@/types'
import { useKeyboardShortcutsForUndoRedo } from '@@/vuecomposables/keyboard_shortcuts'

export const DELETE_SNACKBAR_TIMEOUT = 5000
const UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS = __('Undo (Ctrl+Z)')
const UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC = __('Undo (Cmd+Z)')
const UNDO_DELETE_ACTION_TEXT_MOBILE = __('Undo')

export const getUndoDeleteActionText = (): string => {
  if (!device.desktop) {
    return UNDO_DELETE_ACTION_TEXT_MOBILE
  }
  return device.windows ? UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS : UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC
}

export const useUndoable = (): {
  registerKeyboardShortcuts: () => void
  extractDraftDataForUndoRedo: (draft: DraftPost) => DraftPost
  extractPostDataForUndoRedo: (post: Post) => Post | null
} => {
  const surfaceStore = useSurfaceStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()

  function handleUndo(event: KeyboardEvent): void {
    // Don't trigger undo if the user has an active draft
    if (surfaceDraftsStore.isAnyDraftActive) return

    const action = undo()
    if (action == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        surfaceDraftsStore.undoDeleteDrafts(action.data, { showSnackbar: true })
        break
      case ActionHistoryType.DELETE_POSTS:
        event.preventDefault()
        surfacePostsStore.undoDeletePosts(action.data, { showSnackbar: true })
        break
    }
  }

  function handleRedo(event: KeyboardEvent): void {
    // We don't check for active draft here unlike handleUndo because the user might want to redo their restored draft that is active
    const action = redo()
    if (action == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        void surfaceDraftsStore.redoDeleteDrafts(action.data, { showSnackbar: true })
        break
      case ActionHistoryType.DELETE_POSTS:
        surfacePostsStore.redoDeletePosts(action.data)
        break
    }
  }

  function registerKeyboardShortcuts(): void {
    useKeyboardShortcutsForUndoRedo({
      handleUndo,
      handleRedo,
    })
  }

  function extractDraftDataForUndoRedo(draft: DraftPost): DraftPost {
    // the undo action will recreate id and draftCreatedAt, so we remove it to avoid duplicates
    // remove attachment related fields because they cannot be supported
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { id, draftCreatedAt, attachment, attachment_caption, attachment_link, wish_content, ...draftData } = draft
    return draftData
  }

  function extractPostDataForUndoRedo(post: Post): Post | null {
    // we should not support undo for posts created by other users currently because the recreated post will
    // have the current user as the author, and this will break the post author's permission check
    if (post.author_id !== surfaceStore.user.id) {
      return null
    }
    // the undo action will recreate id and published_at, so we remove it to avoid duplicates
    // remove attachment related fields because they cannot be supported
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { id, published_at, attachment, attachment_caption, attachment_link, wish_content, ...postData } = post
    return postData
  }

  return {
    registerKeyboardShortcuts,
    extractDraftDataForUndoRedo,
    extractPostDataForUndoRedo,
  }
}
